<script setup lang="ts">
import { AppHeader } from '@/widgets/header/index';
import { AppFooter } from '@/widgets/footer/index';
import { useCartStore } from '@/entities/cart/index';
import { useCategoriesStore } from '@/entities/categories/index';
import { useUserStore } from '@/entities/user/index';
import { useModalsStore } from '@/app/store/modals';
import AuthForm from '@/features/AuthForm/ui/index.vue';
import { useLocaleSlugSwitch } from '@/shared/composables/useLocaleSlugSwitch';
import type { Locales } from '@/shared/model/types/locale';

const MobilePanel = defineAsyncComponent(() =>
  import('@/widgets/mobilePanel/index').then(m => m.default),
);

const Modal = defineAsyncComponent(() =>
  import('@/shared/ui/Modal/index').then(m => m.default),
);

const cartStore = useCartStore();
const userStore = useUserStore();
const categoriesStore = useCategoriesStore();
const modalsStore = useModalsStore();
const { switchToLocale } = useLocaleSlugSwitch();
const platform = useRuntimeConfig().public.platform;
const appLangCookie = useCookie<Locales>('ECOM_LANGUAGE', { path: '/' });
const { locale } = useI18n();

const isAuth: Ref<boolean> = computed(() => userStore.isAuth);
const isMobile: Ref<boolean> = computed(() => platform === 'mobile');

onServerPrefetch(async () => {
  await categoriesStore.getAllCategories();

  if (isAuth.value) {
    await cartStore.getCart();
  }
});

provide('isMobile', isMobile);

watch(appLangCookie, async (newLocale, oldLocale) => {
  if (newLocale !== oldLocale) {
    await categoriesStore.getAllCategories();
    await switchToLocale(newLocale);
  }
});
</script>

<template>
  <div :class="['page-wrap', { _mobile: isMobile }]">
    <AppHeader />
    <main class="page-wrap__main">
      <slot />
    </main>
    <AppFooter />
    <MobilePanel v-if="platform === 'mobile'" />
    <Modal
      :modal-active="modalsStore.isAuthModalOpen"
      class-mod="_auth"
      @close="modalsStore.setModalState('auth', false)"
    >
      <AuthForm />
    </Modal>
  </div>
</template>

<style lang="less">
.page-wrap {
  display: flex;
  flex-direction: column;
  min-height: 100dvh;

  &__main {
    flex: 1;
  }

  &._mobile {
    padding-bottom: 61px;
  }
}

.modal._auth .modal__inner {
  padding: 64px @size-x8 @size-x8;
}

@media @1024 {
  .modal._auth {
    .modal__inner {
      padding: 0;
    }

    .modal__button {
      right: 0;
    }
  }
}
</style>
